import dayjs from 'dayjs';
import { ListItem } from '@/assets/Const';
import { GetMOptionList200ResponseBody, ReferenceValueItem } from 'openapi/omise-craft-core-spec';
import { ConfigContext } from '@/config';

export const getFormatDateYMd = (dateStr: string) => {
  let formatDate = '';
  const regex = /^\d{4}-\d{2}-\d{2}$/;

  if (regex.test(dateStr)) {
    formatDate = dayjs(dateStr).format('YYYY年MM月DD日');
  }

  return formatDate;
};

/**
 * @type IdNamePair
 * idとnameのペア
 * @param {number | null | undefined} id id(key)
 * @param {string} name name(value)
 */
type IdNamePair = { id: number | null | undefined; name: string };

/**
 * id情報から紐づくnameを取得する
 *
 * @param {IdNamePair[] | undefined} ArrData idとnameのペアの配列
 * @param {number | null | undefined} id id情報
 * @returns {string} name情報
 *
 * @remarks 第一引数がundefinedの場合は空文字を返す
 */
export const getNameFromArrayById = (ArrData: IdNamePair[] | undefined, id: number | null | undefined) => {
  let retValue = '';
  const obj = ArrData?.find((v) => v.id == id);
  if (obj) {
    retValue = obj.name;
  }
  return retValue;
};

import { paramSignatureType } from '@/features/properties/hoc/SearchConst';
import { useContext } from 'react';

export const setQueryParam = (arrayVal: object): paramSignatureType => {
  const retValue: paramSignatureType = {};
  for (const [key, value] of Object.entries(arrayVal)) {
    if (Array.isArray(value)) {
      if (value.length === 1 && value[0]['value']) {
        retValue[key] = value[0].value;
      } else {
        retValue[key] = value
          .filter((obj) => (typeof obj.value == 'string' ? obj.value.length > 0 : obj.value != undefined))
          .map((obj) => String(obj.value));
      }
    } else if (typeof value == 'string' && value && value.length > 0) {
      retValue[key] = value;
    } else if (value != undefined) {
      retValue[key] = String(value);
    } else {
      continue;
    }
  }

  return retValue;
};

/**
 * 選択肢マスタから指定したカテゴリIDの選択肢リストを取得する
 * @param mOptions apiレスポンス
 * @param categoryId 絞り込み対象のカテゴリID (src\assets\CategoryIdConst.tsに一覧記載)
 * @returns
 */
export const getOptionListFromCategoryId = (
  mOptions: GetMOptionList200ResponseBody | undefined,
  categoryId: number
): ListItem[] | undefined => {
  let options;
  mOptions?.option_categories.forEach((option) => {
    if (categoryId == option.id) {
      options = option.options;
    }
  });
  if (options) {
    return options;
  } else {
    return undefined;
  }
};

/**
 * 選選択肢基準値マスタから指定した項目の値を取得する
 * @param mOperationCategory apiレスポンス
 * @param sectionName 区分名
 * @returns
 */
export const getReferenceFromSectionName = (
  mOperationCategory: ReferenceValueItem[] | undefined,
  sectionName: string
): string | undefined => {
  let reference;
  mOperationCategory?.forEach((option) => {
    if (sectionName == option.sectionName) {
      reference = option.referenceValue;
    }
  });
  if (reference) {
    return reference;
  } else {
    return undefined;
  }
};

/**
 * 数値をカンマ区切りに変換する
 * @param num
 * @returns
 */
export const numToLocalStr = (num: number | undefined) => {
  if (num === undefined) {
    return undefined;
  } else {
    return num.toLocaleString();
  }
};

/**
 * 公開日時から1ヶ月以内かどうかチェック
 */
export const isWithinOneMonth = (dateString: string) => {
  const givenDate = dayjs(dateString);

  const currentDate = dayjs();

  const oneMonthAgo = currentDate.subtract(1, 'month');

  return givenDate.isAfter(oneMonthAgo) && givenDate.isBefore(currentDate);
};

/**
 * S3のコンテンツ取得用URLをConfigから取得して返却する
 */
export const useOmiseCraftStaticContentsUrl = () => {
  const config = useContext(ConfigContext);
  return config?.omiseCraftStaticContentsUrl ?? '';
};

/**
 * blobファイルをbase64に変換する
 * @param blob blobファイル
 * @returns dataURL
 */
export const blobToBase64Async = (blob: Blob | File) =>
  new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = (e) => {
      if (e.target == null) {
        reject(new Error('cannot convert blob.'));
        return;
      }
      let binary = '';
      const bytes = new Uint8Array(e.target.result as ArrayBuffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      resolve(window.btoa(binary));
    };
    fr.onerror = reject;
    fr.readAsArrayBuffer(blob);
  });

/**
 * 連続した呼び出された場合に処理を実行する間隔を制限するthrottle関数を作成する
 * @param callback 関数
 * @param delay 間隔[ms]
 * @returns throttle
 */
export const throttle = <T extends (...args: never[]) => unknown>(callback: T, delay: number) => {
  let timerID: ReturnType<typeof setTimeout>;
  let lastTime: number;
  return (...args: Parameters<T>) => {
    const fireFn = () => {
      callback(...args);
      lastTime = Date.now();
    };
    if (lastTime == undefined) {
      fireFn();
    } else {
      clearTimeout(timerID);
      timerID = setTimeout(fireFn, delay - Date.now() + lastTime);
    }
  };
};

export const prioritizePrefectures = (prefectures: ListItem[]): ListItem[] => {
  const priorityList = ['東京都', '神奈川県', '埼玉県', '千葉県', '福岡県'];

  return [...prefectures].sort((a, b) => {
    const indexA = priorityList.indexOf(a.name);
    const indexB = priorityList.indexOf(b.name);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

export const sortByListOrder = (list: string[], priorityList: ListItem[] | undefined): string[] => {
  if (priorityList == undefined) {
    return list;
  }
  return list.sort((a, b) => {
    const indexA = priorityList.findIndex((item) => item.name === a);
    const indexB = priorityList.findIndex((item) => item.name === b);

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;

    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return a.localeCompare(b);
  });
};
